import React, { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { getRouteUrl } from '@utils/urls';

import { giftCardsPageDataShape } from '@common/types/giftCards';
import { HOME, GIFT_CARDS_VIEW } from '@common/routes';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import ModeToggle from '@common/components/ModeToggle';
import { GIFT_CARDS_VIEW_MODE } from '@common/constants/giftCards';

import GiftCardBalance from './GiftCardBalance';
import GiftCardsPurchase from './GiftCardsPurchase';

const GiftCards = ({ pageData }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { query: { view } = {}, locale } = router;
  const [currentView, setCurrentView] = useState(view);
  const metaTitle =
    view === GIFT_CARDS_VIEW_MODE[locale].PURCHASE
      ? pageData.meta.metaTitle
      : t('giftCards.check.title', 'Kartensaldo abfragen');

  const changeType = useCallback(
    nextView => {
      setCurrentView(nextView);

      setTimeout(() => {
        router.push(
          {
            pathname: GIFT_CARDS_VIEW,
            query: {
              view: nextView,
            },
          },
          getRouteUrl(locale, GIFT_CARDS_VIEW, { view: nextView }),
          {
            shallow: true,
          },
        );
      }, 350);
    },
    [locale, router],
  );

  const isPurchasePage = view === GIFT_CARDS_VIEW_MODE[locale].PURCHASE;

  return (
    <Layout
      description={pageData.meta.metaDescription}
      title={metaTitle}
      canonicalPageUrl={getRouteUrl(locale, GIFT_CARDS_VIEW, { view })}
      ogImage={pageData.meta.metaImage.url}
      ogImageWidth={pageData.meta.metaImage.width}
      ogImageHeight={pageData.meta.metaImage.height}
    >
      <RouteLayout
        backRoute={HOME}
        backRouteAs={getRouteUrl(locale, HOME)}
        columns={12}
        alwaysVisible={!isPurchasePage}
        contentSpacingTop={!isPurchasePage && ['equal', 2]}
        additionalControls={
          <ModeToggle
            options={[
              {
                label: t('giftCards.switcher.order', 'Bestellen'),
                value: GIFT_CARDS_VIEW_MODE[locale].PURCHASE,
              },
              {
                label: t('giftCards.switcher.check', 'Saldo abfragen'),
                value: GIFT_CARDS_VIEW_MODE[locale].BALANCE,
              },
            ]}
            value={currentView}
            onChange={changeType}
            blurredLayout={isPurchasePage}
          />
        }
        isHero={isPurchasePage}
        forceStickyOnMobileApp
      >
        {view === GIFT_CARDS_VIEW_MODE[locale].BALANCE ? (
          <GiftCardBalance />
        ) : (
          <GiftCardsPurchase pageData={pageData} />
        )}
      </RouteLayout>
    </Layout>
  );
};

GiftCards.propTypes = {
  pageData: giftCardsPageDataShape.isRequired,
};

export default GiftCards;
